import {
  BrandIcon,
  MediaFilesIcon,
  NewsMediaIcon,
  ProjectsIcon,
  RelevantPeopleIcon,
  StateCircleIcon,
  TagsIcon,
  TIcon,
  WorkspaceIcon,
} from '@acceso/design-system';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { REVIEW_STATES, TReviewStates } from '@/constants';
import { useInformationUnitTags } from '@/features/app/api/get-information-unit-tags';
import { useProjects } from '@/features/app/api/get-projects';
import { useWorkspaces } from '@/features/app/api/get-workspaces';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { useSelectedFeaturedPeople } from '@/features/featured-people/api/get-featured-people';
import { useSelectedLogos } from '@/features/logos/api/get-logos';
import { reviewStatesValueToLabel } from '@/features/mentions/util/review-helper';
import { useGetPrograms } from '@/features/programs/api/get-programs';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';

export type SelectedFilters = {
  icon: TIcon;
  items: (string | undefined)[];
};
export type TMentionShowSelectedFiltersStateReturn = {
  selectedFilters: SelectedFilters[];
  onCleanFilters: () => void;
};

type UseMentionShowSelectedFiltersStateProps = {
  reviewStateOptions?: TReviewStates[];
};

export function useMentionShowSelectedFiltersState({
  reviewStateOptions = REVIEW_STATES,
}: UseMentionShowSelectedFiltersStateProps = {}): TMentionShowSelectedFiltersStateReturn {
  const {
    review_states: reviewSelected,
    people,
    logos,
    programs,
    broadcasters,
    workspaces,
    tags,
    projects,
    clearFilters: reviewClear,
  } = useProgramBroadcastFilters();

  const { data: { data: programsData = [] } = {} } = useGetPrograms();
  // Resolved by id rather than by scanning the catalogue: a selected logo or person can live
  // on any page of it, and this summary must name it without loading the whole thing.
  const { data: { data: logosData = [] } = {} } = useSelectedLogos(logos);
  const { data: { data: peopleData = [] } = {} } = useSelectedFeaturedPeople(people);
  const { data: { data: channels = [] } = {} } = useChannels();
  const { data: workspacesData = [] } = useWorkspaces();
  const { data: tagsData = [] } = useInformationUnitTags();
  const { data: projectsData = [] } = useProjects();
  const { t } = useTranslation();

  const projectItems = useMemo(() => {
    return projects.map(id => {
      const project = projectsData.find(project => project.id === id);
      return project?.name;
    });
  }, [projects, projectsData]);

  const tagItems = useMemo(() => {
    return tags.map(id => {
      const tag = tagsData.find(tag => tag.id === id);
      return tag?.name;
    });
  }, [tags, tagsData]);

  const workspaceItems = useMemo(() => {
    return workspaces.map(id => {
      const workspace = workspacesData.find(workspace => workspace.id === id);
      return workspace?.name;
    });
  }, [workspaces, workspacesData]);

  const programItems = useMemo(() => {
    return programs.map(id => {
      const program = programsData.find(program => program.id === id);
      return program?.name;
    });
  }, [programs, programsData]);

  const logoItems = useMemo(() => {
    return logos.map(id => {
      const logo = logosData.find(logo => logo.id === id);
      return logo?.name;
    });
  }, [logos, logosData]);

  const peopleItems = useMemo(() => {
    return people.map(id => {
      const person = peopleData.find(person => person.id === id);
      return person?.name;
    });
  }, [people, peopleData]);

  const reviewItems = useMemo(() => {
    const labels = reviewSelected.map(id => reviewStatesValueToLabel(id, reviewStateOptions));
    return labels.map(label => {
      if (label) {
        return t(label);
      }
      return '';
    });
  }, [reviewSelected]);

  const channelItems = useMemo(() => {
    return broadcasters.map(id => {
      const channel = channels.find(channel => channel.id === id);
      return channel?.name;
    });
  }, [broadcasters, channels]);

  const selectedFilters = useMemo(
    () => [
      {
        icon: StateCircleIcon,
        items: reviewItems,
      },
      {
        icon: NewsMediaIcon,
        items: channelItems,
      },
      {
        icon: WorkspaceIcon,
        items: workspaceItems,
      },
      {
        icon: ProjectsIcon,
        items: projectItems,
      },
      {
        icon: TagsIcon,
        items: tagItems,
      },
      {
        icon: MediaFilesIcon,
        items: programItems,
      },
      {
        icon: BrandIcon,
        items: logoItems,
      },
      {
        icon: RelevantPeopleIcon,
        items: peopleItems,
      },
    ],
    [
      reviewItems,
      channelItems,
      workspaceItems,
      projectItems,
      tagItems,
      programItems,
      logoItems,
      peopleItems,
    ]
  );

  const onCleanFilters = useCallback(() => {
    reviewClear();
  }, [reviewClear]);

  return { selectedFilters, onCleanFilters };
}
